import {
  QuestionGroupType,
  QuestionOwnerType,
  TagCategory,
  type SearchQuestionsForQuestionReviewTaskQuery,
} from '@exam-management/graphql';

import type { TaskManagementQuestionListType } from '../../components/task-management-question-list-data-grid';

type SearchQuestion =
  SearchQuestionsForQuestionReviewTaskQuery['qctSearchQuestions']['questions'][number];

interface CreateQuestionRowsOptions {
  questions: SearchQuestion[];
  chapterName: string;
  internalUserEmailDomains: string[];
}

const DIFFICULTY_MAP = {
  ง่าย: 'easy',
  กลาง: 'normal',
  ยาก: 'hard',
} as const;

function getOwnerEmail(question: SearchQuestion, internalUserEmailDomains: string[]) {
  if (question.metadata.owner?.type !== QuestionOwnerType.Internal) {
    return '';
  }

  const domain = internalUserEmailDomains[0];

  return domain ? `a@${domain}` : '';
}

function getQuestionType(question: SearchQuestion) {
  return (
    question.tags.find(
      (tag: SearchQuestion['tags'][number]) => tag.category === TagCategory.Calculation,
    )?.name ?? '-'
  );
}

function getQuestionDifficulty(
  question: SearchQuestion,
): TaskManagementQuestionListType['difficulty'] {
  const difficultyName =
    question.tags.find(
      (tag: SearchQuestion['tags'][number]) => tag.category === TagCategory.Difficulty,
    )?.name ?? '';

  return DIFFICULTY_MAP[difficultyName as keyof typeof DIFFICULTY_MAP] ?? '-';
}

function createItemSetQuestionIdsByGroupId(questions: SearchQuestion[]) {
  return questions.reduce<Record<string, string[]>>((accumulator, question) => {
    if (question.questionGroup?.type !== QuestionGroupType.ItemSet || !question.questionGroup.id) {
      return accumulator;
    }

    const questionIds = accumulator[question.questionGroup.id] ?? [];

    questionIds.push(question.id);
    accumulator[question.questionGroup.id] = questionIds;

    return accumulator;
  }, {});
}

export function createSelectedQuestionRows({
  questions,
  chapterName,
  internalUserEmailDomains,
}: Readonly<CreateQuestionRowsOptions>): TaskManagementQuestionListType[] {
  const itemSetQuestionIdsByGroupId = createItemSetQuestionIdsByGroupId(questions);

  return [...questions]
    .sort((firstQuestion, secondQuestion) =>
      firstQuestion.refCode.localeCompare(secondQuestion.refCode, undefined, { numeric: true }),
    )
    .map((question, index) => {
      const isItemSet = question.questionGroup?.type === QuestionGroupType.ItemSet;
      const questionGroupId = isItemSet ? (question.questionGroup?.id ?? null) : null;

      return {
        id: question.id,
        taskId: null,
        qBankStructureId: question.refQBankStructureId,
        order: index + 1,
        code: question.refCode,
        chapterName,
        questionGroupType: question.questionGroup?.type ?? QuestionGroupType.Single,
        subQuestionCount: question.questionGroup?.questions?.length,
        questionGroupId,
        questionIds: questionGroupId
          ? (itemSetQuestionIdsByGroupId[questionGroupId] ?? [question.id])
          : [question.id],
        type: getQuestionType(question),
        itemSetName: isItemSet ? question.questionGroup?.name : '-',
        difficulty: getQuestionDifficulty(question),
        createdBy: {
          fullName: question.metadata.owner?.value ?? '',
          email: getOwnerEmail(question, internalUserEmailDomains),
        },
      };
    });
}

export function removeQuestionById(
  selectedQuestionMap: Record<string, boolean | null | undefined>,
  questionId: string,
) {
  const nextSelectedQuestionMap = { ...selectedQuestionMap };

  delete nextSelectedQuestionMap[questionId];

  return nextSelectedQuestionMap;
}

export function removeItemSetQuestionIds(
  selectedQuestionMap: Record<string, boolean | null | undefined>,
  questionIds: string[],
) {
  const nextSelectedQuestionMap = { ...selectedQuestionMap };

  questionIds.forEach((questionId) => {
    delete nextSelectedQuestionMap[questionId];
  });

  return nextSelectedQuestionMap;
}
